import React, { useEffect, useRef, useState } from 'react';
import { X, Copy, Check, Loader2, AlertTriangle } from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { track } from '../../lib/track';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

interface Props {
  user: User;
  onClose: () => void;
}

const CONFIRM_PHRASE = 'ZERAR MEU ALBUM';

export function ResetAlbumModal({ user, onClose }: Props) {
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [password, setPassword] = useState('');
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useBodyScrollLock(true);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [loading, onClose]);

  const canSubmit = confirmPhrase.trim() === CONFIRM_PHRASE && password.length > 0 && !loading;

  const handleBackdropClick = () => {
    if (!loading && confirmPhrase.length === 0) onClose();
  };

  const handleCopy = async () => {
    setCopying(true);
    try {
      const { data } = await supabase
        .from('stickers')
        .select('sticker_code, count')
        .eq('user_id', user.id);

      const rows = (data ?? []).sort((a, b) => a.sticker_code.localeCompare(b.sticker_code));
      const tenho = rows.filter((r) => r.count >= 1);
      const repetidas = rows.filter((r) => r.count >= 2);
      const today = new Date().toLocaleDateString('pt-BR');

      const text = [
        `Minhas figurinhas - BoraTrocar (${today})`,
        '',
        `Tenho (${tenho.length}):`,
        tenho.length > 0 ? tenho.map((r) => r.sticker_code).join(', ') : '(nenhuma)',
        '',
        `Repetidas (${repetidas.length}):`,
        repetidas.length > 0
          ? repetidas
              .map((r) => {
                const extras = r.count - 1;
                return extras === 1 ? r.sticker_code : `${r.sticker_code} x${extras}`;
              })
              .join(', ')
          : '(nenhuma)',
      ].join('\n');

      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } finally {
      setCopying(false);
    }
  };

  const handleReset = async () => {
    setError(null);
    setLoading(true);

    try {
      // AVISO: esta reautenticação assume que o usuário tem senha no Supabase
      // (login via e-mail e senha). Se um dia o BoraTrocar adicionar login
      // social (Google, Apple etc), este fluxo precisa ser revisto:
      // usuários OAuth NÃO têm senha e signInWithPassword vai falhar para eles.
      // Nesse caso, detectar user.app_metadata.provider e, se for OAuth,
      // exigir re-login pelo mesmo provedor antes de chamar reset_user_album.
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password,
      });

      if (authError) {
        setError('Senha incorreta.');
        setLoading(false);
        return;
      }

      const { data, error: rpcError } = await supabase.rpc('reset_user_album');

      if (rpcError || !data?.ok) {
        if (data?.error === 'too_soon') {
          setError(
            'Você atingiu o limite de 3 zeramentos por dia. Tente novamente amanhã.'
          );
        } else {
          setError('Não foi possível zerar o álbum agora. Tente novamente.');
        }
        setLoading(false);
        return;
      }

      void track('account_reset', { user_id: user.id });
      onClose();
      window.location.assign('/album');
    } catch {
      setError('Não foi possível zerar o álbum agora. Tente novamente.');
      setLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-album-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-6"
      onClick={handleBackdropClick}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-lg max-h-[90vh] overflow-y-auto animate-fade-up"
      >
        <div className="sticky top-0 bg-white rounded-t-2xl border-b border-ink-100 px-6 py-4 flex items-center justify-between">
          <h2
            id="reset-album-title"
            className="font-display font-semibold text-h3 text-ink-900"
          >
            Zerar álbum
          </h2>
          <button
            type="button"
            onClick={() => {
              if (!loading) onClose();
            }}
            aria-label="Fechar"
            disabled={loading}
            className="p-2 rounded-full text-ink-500 hover:text-ink-900 hover:bg-ink-100 disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
          <div className="flex items-start gap-3 rounded-lg border border-ruby-300 bg-ruby-50 px-4 py-3">
            <AlertTriangle
              size={18}
              className="text-ruby-700 shrink-0 mt-0.5"
              strokeWidth={2}
            />
            <p className="text-small font-display font-semibold text-ruby-700">
              Esta ação não pode ser desfeita.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-small">
            <div className="rounded-lg border border-ruby-100 bg-ruby-50 p-3">
              <p className="font-display font-semibold text-ruby-700 mb-1">Será apagado</p>
              <p className="text-ink-700 leading-snug">
                Figurinhas marcadas, wishlist, feed de novidades e conquistas.
              </p>
            </div>
            <div className="rounded-lg border border-ink-100 bg-paper p-3">
              <p className="font-display font-semibold text-ink-700 mb-1">Será preservado</p>
              <p className="text-ink-500 leading-snug">
                Amigos, pedidos pendentes, trocas históricas e seu perfil.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCopy}
            disabled={copying}
            className="w-full flex items-center justify-center gap-2 rounded-lg border border-ink-300 bg-white hover:bg-ink-100 text-ink-700 text-small font-display font-semibold h-10 px-4 btn-focus disabled:opacity-50 transition-colors"
          >
            {copying ? (
              <>
                <Loader2 size={15} className="animate-spin" /> Copiando...
              </>
            ) : copied ? (
              <>
                <Check size={15} className="text-green-700" /> Copiado!
              </>
            ) : (
              <>
                <Copy size={15} /> Copiar minhas figurinhas
              </>
            )}
          </button>

          <div>
            <label className="block text-small font-display font-semibold text-ink-900 mb-1.5">
              Para confirmar, digite{' '}
              <span className="font-mono font-bold text-ruby-700">ZERAR MEU ALBUM</span>
            </label>
            <input
              type="text"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              placeholder="ZERAR MEU ALBUM"
              autoComplete="off"
              className="w-full h-11 px-3 rounded-md border-[1.5px] border-ink-300 focus:border-ruby-500 outline-none font-body text-body text-ink-900"
            />
          </div>

          <div>
            <label className="block text-small font-display font-semibold text-ink-900 mb-1.5">
              Confirme sua senha
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full h-11 px-3 rounded-md border-[1.5px] border-ink-300 focus:border-ruby-500 outline-none font-body text-body text-ink-900"
            />
          </div>

          {error && (
            <div className="rounded-lg border border-ruby-500 bg-ruby-100 px-3 py-2 text-small text-ruby-700 flex items-center gap-2">
              <AlertTriangle size={14} strokeWidth={2} className="shrink-0" />
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
            <button
              ref={cancelRef}
              type="button"
              onClick={() => {
                if (!loading) onClose();
              }}
              disabled={loading}
              className="flex-1 h-11 rounded-md border-[1.5px] border-ink-300 font-display font-semibold text-ink-700 hover:bg-ink-100 btn-focus disabled:opacity-40 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={!canSubmit}
              className="flex-1 h-11 rounded-md font-display font-semibold text-white bg-ruby-600 hover:bg-ruby-700 disabled:bg-ink-300 disabled:cursor-not-allowed btn-focus inline-flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 size={15} className="animate-spin" /> Zerando...
                </>
              ) : (
                'Zerar álbum'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
